import { InfoCard, Link, Progress } from '@backstage/core-components';
import { Box, Typography, makeStyles } from '@material-ui/core';
import { updatedLabel } from '../../home/updatedLabel';
import {
  MARK_LOOK,
  missedTeamRules,
  repoRowLinkOf,
  type RepoStandard,
} from '../../scorecards/repositories';
import { visuallyHidden } from '../../visuallyHidden';
import { useInsights } from './useInsights';

const useStyles = makeStyles(theme => ({
  verdict: { display: 'block', fontSize: '0.875rem', color: 'var(--ui-text)' },
  list: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
    display: 'grid',
    gap: theme.spacing(0.75),
    '& li': {
      display: 'grid',
      gridTemplateColumns: '1.25rem minmax(10rem, 16rem) 1fr',
      gap: theme.spacing(1),
      alignItems: 'baseline',
      [theme.breakpoints.down('xs')]: {
        gridTemplateColumns: '1.25rem 1fr',
      },
    },
  },
  glyph: { fontWeight: 700, textAlign: 'center' },
  rule: { fontWeight: 600 },
  section: { fontWeight: 400, color: 'var(--ui-text-muted)' },
  detail: {
    color: 'var(--ui-text-muted)',
    overflowWrap: 'anywhere',
    [theme.breakpoints.down('xs')]: { gridColumn: '2' },
  },
  foot: {
    marginTop: theme.spacing(1.5),
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
  },
  hidden: visuallyHidden,
}));

/**
 * The CI/CD standard on a component's CI/CD tab (DX-225,
 * specs/repository-standard.md AC-20): its repository's row from the
 * Repositories tab — the same marks, read from the same measurement — and the
 * way to that row. A library and a website carry it as a service does: the
 * standard is about the repository, whatever it holds.
 */
export const RepositoryStandardCard = ({ slug }: { slug: string }) => {
  const classes = useStyles();
  const [owner, repo] = slug.split('/');
  const answer = useInsights<RepoStandard>(
    `/repositories/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}`,
  );
  const action = (
    <Box pt={1} pr={1}>
      <Link to={repoRowLinkOf(slug)}>Open on the Repositories tab</Link>
    </Box>
  );

  if (answer.loading) {
    return (
      <InfoCard title="CI/CD standard">
        <Progress />
      </InfoCard>
    );
  }
  if (answer.error || !answer.data) {
    return (
      <InfoCard title="CI/CD standard">
        <Typography role="alert">
          Couldn't load the CI/CD standard for {slug}.
        </Typography>
      </InfoCard>
    );
  }
  const { generatedAt, rules, orgs, repo: row } = answer.data;
  if (generatedAt === null) {
    return (
      <InfoCard title="CI/CD standard">
        <Typography>
          The repositories have not been measured yet since the portal started.
        </Typography>
      </InfoCard>
    );
  }
  if (!row) {
    // An organisation that could not be listed is not where the repository
    // stands, and the card says which of the two it is (AC-20).
    const unlisted = orgs.some(
      o => o.state === 'unread' && o.org.toLowerCase() === owner.toLowerCase(),
    );
    return (
      <InfoCard title="CI/CD standard">
        <Typography>
          {unlisted
            ? `${slug} is not measured — the repositories of ${owner} could not be listed.`
            : `${slug} is not measured — archived, a fork, or outside the organisations the portal reads.`}
        </Typography>
      </InfoCard>
    );
  }

  const missed = missedTeamRules(row, rules);
  let verdict = 'Meets every rule its team can fix';
  if (row.empty) verdict = 'Empty — exempt until it is archived or filled';
  else if (missed.length === 1) verdict = 'Misses 1 rule its team can fix';
  else if (missed.length > 1) {
    verdict = `Misses ${missed.length} rules its team can fix`;
  } else if (row.teamRulesMet === null) {
    verdict = 'Not every rule its team can fix could be measured';
  }

  return (
    <InfoCard
      title="CI/CD standard"
      subheader={<span className={classes.verdict}>{verdict}</span>}
      action={action}
    >
      <ul
        className={classes.list}
        aria-label={`What the CI/CD standard found for ${slug}`}
      >
        {rules.map(rule => {
          const mark = row.marks[rule.id];
          const look = MARK_LOOK[mark.state];
          return (
            <li key={rule.id}>
              <span
                className={classes.glyph}
                style={{ color: look.colour }}
                aria-hidden
              >
                {look.glyph}
              </span>
              <span className={classes.rule}>
                {rule.title}{' '}
                <span className={classes.section}>
                  {rule.section}
                  {rule.fixedBy === 'admin' && ' · org admin'}
                </span>
                <span className={classes.hidden}> {look.word}</span>
              </span>
              <span className={classes.detail}>
                {mark.link ? (
                  <Link to={mark.link}>{mark.detail}</Link>
                ) : (
                  mark.detail
                )}
              </span>
            </li>
          );
        })}
      </ul>
      <Typography className={classes.foot}>
        Measured{' '}
        <time dateTime={generatedAt} title={generatedAt}>
          {updatedLabel(generatedAt)}
        </time>
        .
      </Typography>
    </InfoCard>
  );
};
